import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  GroupDto,
  PostDto,
  SessionDto,
  StudentDto,
  UpdateGroupDto,
  UpdatePostDto,
  UpdateStudentDto,
  UploadUrlResponseDto,
  UserDto,
} from '@oracychampions/api-interfaces';

@Injectable({
  providedIn: 'root',
})
export class GroupsService {
  private http = inject(HttpClient);

  refresh$ = new BehaviorSubject(null);
  refreshStudents$ = new BehaviorSubject(null);
  refreshPosts$ = new BehaviorSubject(null);
  refreshTeachers$ = new BehaviorSubject(null);
  refreshSessions$ = new BehaviorSubject(null);

  reloadGroups() {
    this.refresh$.next(null);
  }

  reloadStudents() {
    this.refreshStudents$.next(null);
  }

  reloadPosts() {
    this.refreshPosts$.next(null);
  }

  reloadTeachers() {
    this.refreshTeachers$.next(null);
  }

  reloadSessions() {
    this.refreshSessions$.next(null);
  }

  getStudents(groupId: string): Observable<StudentDto[]> {
    return this.refreshStudents$.pipe(
      switchMap(() => this.http.get<StudentDto[]>(`${environment.API}/groups/${groupId}/students`)),
    );
  }

  createStudent(student: UpdateStudentDto): Observable<StudentDto[]> {
    return this.http.post<StudentDto[]>(`${environment.API}/students`, student);
  }

  updateStudent(id: string, student: UpdateStudentDto): Observable<StudentDto[]> {
    return this.http.patch<StudentDto[]>(`${environment.API}/students/${id}`, student);
  }

  deleteStudent(id: string): Observable<any> {
    return this.http.delete<StudentDto[]>(`${environment.API}/students/${id}`);
  }

  getPosts(groupId: string): Observable<PostDto[]> {
    return this.refreshPosts$.pipe(
      switchMap(() => this.http.get<PostDto[]>(`${environment.API}/groups/${groupId}/posts`)),
    );
  }

  createPost(student: UpdatePostDto): Observable<PostDto[]> {
    return this.http.post<PostDto[]>(`${environment.API}/posts`, student);
  }

  updatePost(id: string, student: UpdatePostDto): Observable<PostDto[]> {
    return this.http.patch<PostDto[]>(`${environment.API}/posts/${id}`, student);
  }

  deletePost(id: string): Observable<any> {
    return this.http.delete<PostDto[]>(`${environment.API}/posts/${id}`);
  }

  getSessions(groupId: string): Observable<SessionDto[]> {
    return this.refreshSessions$.pipe(
      switchMap(() => this.http.get<SessionDto[]>(`${environment.API}/groups/${groupId}/sessions`)),
    );
  }

  createSession(student: UpdatePostDto): Observable<SessionDto[]> {
    return this.http.post<SessionDto[]>(`${environment.API}/sessions`, student);
  }

  updateSession(id: string, student: UpdatePostDto): Observable<SessionDto[]> {
    return this.http.patch<SessionDto[]>(`${environment.API}/sessions/${id}`, student);
  }

  deleteSession(id: string): Observable<any> {
    return this.http.delete<SessionDto[]>(`${environment.API}/sessions/${id}`);
  }

  uploadAudio(audioId: string, blob: Blob) {
    const file = new File([blob], audioId, { type: blob.type, lastModified: Date.now() });
    return this.http
      .get<UploadUrlResponseDto>(`${environment.API}/files/audio/uploadurl?filename=${audioId}&mimetype=${blob.type}`)
      .pipe(
        switchMap(({ url }) => {
          return this.http.put(url, file);
        }),
      );
  }

  transcribe(audioId: string, blob: Blob): Observable<any> {
    const file = new File([blob], audioId, { type: blob.type, lastModified: Date.now() });
    const url = `${environment.API}/ai/transcribe`;
    const data = new FormData();
    data.append('file', file);
    return this.http.post(url, data);
  }

  generateImage(prompt: string) {
    const url = `${environment.API}/ai/generate`;
    return this.http.post(url, { prompt });
  }

  uploadImageUrl(url: string) {
    const apiUrl = `${environment.API}/files/imageurl`;
    return this.http.post(apiUrl, { url });
  }

  getTeachers(groupId: string): Observable<UserDto[]> {
    return this.refreshTeachers$.pipe(
      switchMap(() => this.http.get<UserDto[]>(`${environment.API}/groups/${groupId}/users`)),
    );
  }

  assignTeacher(groupId: string, userId: string): Observable<UserDto[]> {
    return this.http.post<UserDto[]>(`${environment.API}/groups/${groupId}/users`, { userId });
  }

  removeTeacher(groupId: string, userId: string): Observable<UserDto[]> {
    return this.http.delete<UserDto[]>(`${environment.API}/groups/${groupId}/users/${userId}`);
  }

  getGroup(id: string): Observable<GroupDto> {
    return this.refresh$.pipe(switchMap(() => this.http.get<GroupDto>(`${environment.API}/groups/${id}`)));
  }

  getGroups(): Observable<GroupDto[]> {
    return this.refresh$.pipe(switchMap(() => this.http.get<GroupDto[]>(`${environment.API}/groups`)));
  }

  createGroup(group: UpdateGroupDto): Observable<GroupDto[]> {
    return this.http.post<GroupDto[]>(`${environment.API}/groups`, group);
  }

  updateGroup(id: string, group: UpdateGroupDto): Observable<GroupDto[]> {
    return this.http.patch<GroupDto[]>(`${environment.API}/groups/${id}`, group);
  }

  deleteGroup(id: string): Observable<any> {
    return this.http.delete<GroupDto[]>(`${environment.API}/groups/${id}`);
  }
}
